import { Injectable } from '@angular/core';
import * as CryptoJS from 'crypto-js';

import { Credentials } from './credentials';
import { HttpRequest } from './http-request';

@Injectable()
export class AwsSignature {

    signRequest(request: HttpRequest, credentials: Credentials, region: string, service: string, signDate?: Date): any {

        let ws = this.initWorkingset(request, credentials, region, service, signDate);

        this.prepare(ws);
        this.buildCanonicalRequest(ws);    // Step1: build the canonical request
        this.buildStringToSign(ws);        // Step2: build the string to sign
        this.calculateSignature(ws);       // Step3: calculate the signature hash
        this.buildSignatureHeader(ws);     // Step4: build the authorization header

        return {
            'Authorization': ws.authorizationHeader,
            'x-amz-date': ws.request.headers['x-amz-date'],
            'x-amz-security-token': ws.config.credentials.sessionToken || undefined,
            'x-amz-content-sha256': this.hash(ws.request.data)
        };
    };

    signRequestUsingQueryString(request: HttpRequest, credentials: Credentials, region: string, service: string, signDate?: Date): string {

        let ws = this.initWorkingset(request, credentials, region, service, signDate);

        this.prepareForQueryString(ws);
        this.buildCanonicalRequest(ws);    // Step1: build the canonical request
        this.buildStringToSign(ws);        // Step2: build the string to sign
        this.calculateSignature(ws);       // Step3: calculate the signature hash
        this.buildSignatureQueryString(ws); // Step4: build the authorization query string

        let requestUrl: string = null;
        if (request.url.indexOf('?') > -1) {
            requestUrl = request.url + '&' + ws.authorizationQueryString;
        } else {
            requestUrl = request.url + '?' + ws.authorizationQueryString;
        }

        if (ws.config.credentials.sessionToken) {
            requestUrl += '&X-Amz-Security-Token=' + encodeURIComponent(ws.config.credentials.sessionToken);
        }

        return requestUrl;
    };

    private initWorkingset(request: HttpRequest, credentials: Credentials, region: string, service: string, signDate?: Date): SignatureWorkingSet {
        let config = {
            region: region,
            service: service,
            credentials: credentials
        };

        let workingSet = new SignatureWorkingSet();
        workingSet.request = request;
        workingSet.config = config;
        workingSet.signDate = signDate || new Date(Date.now() + window['AWS'].config.systemClockOffset);
        workingSet.uri = this.parseUri(request.url);

        if (!workingSet.request.data) {
            workingSet.request.data = '';
        }

        workingSet.request.method = workingSet.request.method.toUpperCase();

        return workingSet;
    }

    private prepare(ws: SignatureWorkingSet) {
        let headers = {
            'host': ws.uri.host,
            'x-amz-date': this.amzDate(ws.signDate)
        };

        if (ws.config.credentials.sessionToken) {
            headers['x-amz-security-token'] = ws.config.credentials.sessionToken;
        }

        headers['x-amz-content-sha256'] = this.hash(ws.request.data);

        // Headers
        ws.request.headers = AwsSignature.extend(
            headers,
            Object.keys(ws.request.headers || {}).reduce(function (normalized, key) {
                normalized[key.toLowerCase()] = ws.request.headers[key];
                return normalized;
            }, {})
        );
        ws.sortedHeaderKeys = Object.keys(ws.request.headers).sort();
    }

    private prepareForQueryString(ws: SignatureWorkingSet) {
        let headers = {
            'host': ws.uri.host
        };
        ws.request.headers = headers;
        ws.sortedHeaderKeys = Object.keys(ws.request.headers).sort();

        ws.credentialScope = [this.amzDate(ws.signDate, true), ws.config.region, ws.config.service, 'aws4_request'].join('/');
        ws.uri.queryParams['X-Amz-Algorithm'] = 'AWS4-HMAC-SHA256';
        ws.uri.queryParams['X-Amz-Credential'] = ws.config.credentials.accessKeyId + '/' + ws.credentialScope;
        ws.uri.queryParams['X-Amz-Date'] = this.amzDate(ws.signDate);
        ws.uri.queryParams['X-Amz-SignedHeaders'] = 'host';
    }

    // Convert the request to a canonical format.
    private buildCanonicalRequest(ws: SignatureWorkingSet) {
        ws.signedHeaders = ws.sortedHeaderKeys.map(function (key) {
            return key.toLowerCase();
        }).join(';');
        ws.canonicalRequest = String(ws.request.method).toUpperCase() + '\n' +
            // Canonical URI:
            this.uriEncode(ws.uri.path) + '\n' +
            // Canonical Query String:
            Object.keys(ws.uri.queryParams).sort().map(function (key) {
                return encodeURIComponent(key) + '=' +
                    encodeURIComponent(ws.uri.queryParams[key]);
            }).join('&') + '\n' +
            // Canonical Headers:
            ws.sortedHeaderKeys.map(function (key) {
                return key.toLocaleLowerCase() + ':' + ws.request.headers[key];
            }).join('\n') + '\n\n' +
            // Signed Headers:
            ws.signedHeaders + '\n' +
            // Hashed Payload
            this.hash(ws.request.data);
    }

    // Construct the string that will be signed.
    private buildStringToSign(ws: SignatureWorkingSet) {
        if (!ws.credentialScope) {
            ws.credentialScope = [this.amzDate(ws.signDate, true), ws.config.region, ws.config.service, 'aws4_request'].join('/');
        }
        ws.stringToSign = 'AWS4-HMAC-SHA256' + '\n' +
            this.amzDate(ws.signDate) + '\n' +
            ws.credentialScope + '\n' +
            this.hash(ws.canonicalRequest);
    }

    // Calculate the signature
    private calculateSignature(ws: SignatureWorkingSet) {
        var signKey = this.hmac(
            this.hmac(
                this.hmac(
                    this.hmac(
                        'AWS4' + ws.config.credentials.secretAccessKey,
                        this.amzDate(ws.signDate, true),
                        { hexOutput: false }
                    ),
                    ws.config.region,
                    { hexOutput: false, textInput: false }
                ),
                ws.config.service,
                { hexOutput: false, textInput: false }
            ),
            'aws4_request',
            { hexOutput: false, textInput: false }
        );
        ws.signature = this.hmac(signKey, ws.stringToSign, { textInput: false });
    }

    // Build the signature HTTP header using the data in the working set.
    private buildSignatureHeader(ws: SignatureWorkingSet) {
        ws.authorizationHeader = 'AWS4-HMAC-SHA256 ' +
            'Credential=' + ws.config.credentials.accessKeyId + '/' + ws.credentialScope + ',' +
            'SignedHeaders=' + ws.signedHeaders + ',' +
            'Signature=' + ws.signature;
    }

    // Build the signature query string using the data in the working set.
    private buildSignatureQueryString(ws: SignatureWorkingSet) {
        ws.authorizationQueryString = 'X-Amz-Algorithm=AWS4-HMAC-SHA256';
        ws.authorizationQueryString += '&X-Amz-Credential=' + encodeURIComponent(ws.config.credentials.accessKeyId + '/' + ws.credentialScope);
        ws.authorizationQueryString += '&X-Amz-Date=' + this.amzDate(ws.signDate);
        ws.authorizationQueryString += '&X-Amz-SignedHeaders=host';
        ws.authorizationQueryString += '&X-Amz-Signature=' + ws.signature;
    }

    private parseUri(uri: string): Uri {
        let parser: any;
        parser = document ? document.createElement('a') : {};
        parser.href = uri;
        let result = new Uri();
        result.protocol = parser.protocol;
        result.host = parser.host;
        let portIdx = result.host.indexOf(':');
        if (portIdx > 0) {
            result.host = result.host.substr(0, portIdx);
        }
        result.path = ((parser.pathname.charAt(0) !== '/') ? '/' : '') + parser.pathname;
        result.queryParams = this.extractQueryParams(parser.search);
        return result;
    }


    private extractQueryParams(search: string): string {
        return /^\??(.*)$/.exec(search)[1].split('&')
            .reduce(function (result: any, arg: any) {
                arg = /^(.+)=(.*)$/.exec(arg);
                if (arg) {
                    result[arg[1]] = arg[2];
                }
                return result;
            }, {});
    }

    private uriEncode(input: string): string {
        var result = '';
        for (var i = 0; i < input.length; i++) {
            var ch = input.charAt(i);
            if ((ch >= 'A' && ch <= 'Z') || (ch >= 'a' && ch <= 'z') || (ch >= '0' && ch <= '9') || ch == '_' || ch == '-' || ch == '~' || ch == '.' || ch == '/') {
                result = result.concat(ch);
            } else {
                result = result.concat('%' + input.charCodeAt(i).toString(16).toUpperCase());
            }
        }
        return result;
    }

    private amzDate(date: Date, short?: boolean): string {
        let result = date.toISOString().replace(/[:\-]|\.\d{3}/g, '').substr(0, 17);
        if (short) {
            return result.substr(0, 8);
        }
        return result;
    }

    /**
    * Hash the given input using SHA-256 algorithm.
    * The options can be used to control the in-/output of the hash operation.
    * @param {*} input Input data.
    * @param {object} options Options object:
    * `hexOutput` -- Output the hash with hex encoding (default: `true`).
    * `textInput` -- Interpret the input data as text (default: `true`).
    * @returns The generated hash
    */
    private hash(input: string, options?: any): any {
        options = AwsSignature.extend({ hexOutput: true, textInput: true }, options);
        let hash = CryptoJS.SHA256(input);
        if (options.hexOutput) {
            return CryptoJS.enc.Hex.stringify(hash);
        }
        return hash;
    }

    /**
     * Create the HMAC of the given input data with the given key using the SHA-256
     * hash algorithm.
     * The options can be used to control the in-/output of the hash operation.
     * @param {string} key Secret key.
     * @param {*} input Input data.
     * @param {object} options Options object:
     * `hexOutput` -- Output the hash with hex encoding (default: `true`).
     * `textInput` -- Interpret the input data as text (default: `true`).
     * @returns The generated HMAC.
     */
    private hmac(key: string, input: string, options?: any): any {
        options = AwsSignature.extend({ hexOutput: true, textInput: true }, options);
        let hmac = CryptoJS.HmacSHA256(input, key, { asBytes: true });
        if (options.hexOutput) {
            return CryptoJS.enc.Hex.stringify(hmac);
        }
        return hmac;
    }

    private static extend(dest: any, ...objs: any[]) {
        objs.forEach(function (obj: any) {
            if (!obj || typeof (obj) !== 'object') {
                return;
            }
            Object.keys(obj).forEach(function (key) {
                let src = obj[key];
                if (typeof (src) === 'undefined') {
                    return;
                }
                if (src !== null && typeof (src) === 'object') {
                    dest[key] = (Array.isArray(src) ? [] : {});
                    AwsSignature.extend(dest[key], src);
                } else {
                    dest[key] = src;
                }
            });
        });
        return dest;
    }
}

class SignatureConfig {
    region: string;
    service: string;
    credentials: Credentials;
}

class SignatureWorkingSet {
    request: HttpRequest;
    config: SignatureConfig;
    signDate: Date;
    uri: Uri;
    sortedHeaderKeys: string[];
    signedHeaders: string;
    canonicalRequest: string;
    credentialScope: string;
    stringToSign: string;
    signature: string;
    authorizationHeader: string;
    authorizationQueryString: string
}

class Uri {
    protocol: string;
    host: string;
    path: string;
    queryParams: string;
}



// WEBPACK FOOTER //
// ./src/app/aws/signature.ts